import { z } from "zod";

/**
 * Properties/units shared contract (Strangler-Fig migration, manifest-6 +
 * docs/design/cache-layer.md Phase 2).
 *
 * Single source of truth for the wire shapes shared by the web SPA
 * (`src/repositories/properties.ts`, `src/repositories/units.ts`) and the
 * NestJS slices (`apps/api/src/properties/*`, `apps/api/src/units/*`).
 * snake_case so the FE↔Nest transport is a field-for-field swap.
 *
 * All object schemas are `.passthrough()` (conventions §8): the server can add
 * fields without breaking deployed SPAs; the FE parse is a shape GATE (id must
 * exist), not a strict validator. The full column set rides through untyped —
 * the SPA's generated Supabase Row types remain the field-level authority.
 */

/** Mini owner embedded in cards/lists. Carries the commercial-entity +
 *  representative fields so the FE resolves an organization owner's
 *  establishment name (getOwnerDisplayName) rather than the contact person. */
export const ownerMiniSchema = z
  .object({
    id: z.string(),
    full_name: z.string().nullable().optional(),
    full_name_en: z.string().nullable().optional(),
    owner_type: z.string().nullable().optional(),
    commercial_name: z.string().nullable().optional(),
    commercial_name_en: z.string().nullable().optional(),
    representative_name: z.string().nullable().optional(),
    representative_name_en: z.string().nullable().optional(),
  })
  .passthrough();

/** A bare `properties` row as returned by the Nest write endpoints. */
export const propertyRowSchema = z.object({ id: z.string() }).passthrough();

/** A bare `units` row as returned by the Nest write endpoints. */
export const unitRowSchema = z.object({ id: z.string() }).passthrough();

/** Unit mini embedded in a property card (superset of the FE LIST_SELECT's units). */
export const propertyCardUnitSchema = z
  .object({
    id: z.string(),
    status: z.string().nullable().optional(),
    images: z.unknown().optional(),
    owner: ownerMiniSchema.nullable().optional(),
  })
  .passthrough();

/**
 * The server-owned property card (GET /properties/cards) — design §1.3.
 * `unit_count`/`rented_count` are precomputed over ACTIVE units. The card is a
 * SUPERSET of the legacy FE list row (`*, units(images, owner), owner`), so the
 * flipped repository can serve it to existing consumers unchanged.
 */
export const propertyCardSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
    owner: ownerMiniSchema.nullable().optional(),
    units: z.array(propertyCardUnitSchema).optional(),
    unit_count: z.number().optional(),
    rented_count: z.number().optional(),
    /** Derived per read on the server from cached active-contract facts. */
    monthly_rent: z.number().optional(),
  })
  .passthrough();

/** GET /properties/stats — the dashboard category breakdown (Phase 3). */
export const propertyStatsSchema = z
  .object({
    total: z.number(),
    residential: z.number(),
    commercial: z.number(),
    industrial: z.number(),
    land: z.number(),
    mixed: z.number(),
  })
  .passthrough();

/** GET /units/stats — the dashboard unit-status breakdown (Phase 3). */
export const unitStatsSchema = z
  .object({
    total: z.number(),
    available: z.number(),
    rented: z.number(),
    maintenance: z.number(),
  })
  .passthrough();

export type OwnerMini = z.infer<typeof ownerMiniSchema>;
export type PropertyCard = z.infer<typeof propertyCardSchema>;
export type PropertyStats = z.infer<typeof propertyStatsSchema>;
export type UnitStats = z.infer<typeof unitStatsSchema>;
